// PartA3TimeManagement.js - Time Management component for Part A3
import React from 'react';
import { Input, Radio, Row, Col } from 'antd';
import { ClockCircleOutlined } from '@ant-design/icons';

const PartA3TimeManagement = ({ formData, handleInputChange }) => {
    const statements = [
        { key: 'follow_schedule', text: 'I like following a schedule.' },
        { key: 'get_distracted', text: 'I get distracted and do not start my tasks on time.' },
        { key: 'last_minute', text: 'I do things at the last minute.' },
        { key: 'figure_time', text: 'I can figure out how much time assignments and projects will take.' },
        { key: 'decide_tasks', text: 'I am good at deciding which tasks to do first.' }
    ];

    const options = [
        { label: 'Rarely', value: 'rarely' },
        { label: 'Sometimes', value: 'sometimes' },
        { label: 'Usually', value: 'usually' },
        { label: 'Almost always', value: 'almost_always' }
    ];

    return (
        <div className="a4-page font-[poppins] bg-white p-8 mx-auto text-[#333333]" style={{ maxWidth: '100%', width: '100%' }}>
            {/* Header */}
            <div className="text-center mb-6">
                <h2 className="text-xl font-bold text-[#F97316] mb-1">PART A (3)</h2>
                <h3 className="text-lg font-bold text-black">Time Management</h3>
            </div>

            <div className="font-bold text-sm mb-4">Tick (✓) the most appropriate option.</div>

            {/* Statements Table */}
            <div className="mb-8 border-2 border-[#F97316]/30 rounded-lg overflow-hidden">
                <div className="grid grid-cols-5 bg-[#FFEDD5] border-b border-[#F97316]/30">
                    <div className="col-span-1 p-3 font-bold text-center border-r border-[#F97316]/30 flex items-center justify-center">Statements</div>
                    {options.map((opt) => (
                        <div key={opt.value} className="p-3 font-bold text-center border-r border-[#F97316]/30 last:border-r-0 text-sm">
                            {opt.label}
                        </div>
                    ))}
                </div>

                {statements.map((statement, index) => (
                    <div key={statement.key} className="grid grid-cols-5 border-b border-[#F97316]/30 last:border-b-0 bg-white hover:bg-orange-50 transition-colors">
                        <div className="col-span-1 p-3 border-r border-[#F97316]/30 text-sm font-medium flex items-start">
                            <span className="mr-2 font-bold text-[#F97316]">{index + 1}.</span>
                            {statement.text}
                        </div>
                        {options.map((opt) => (
                            <div key={opt.value} className="p-3 border-r border-[#F97316]/30 last:border-r-0 flex items-center justify-center">
                                <Radio
                                    checked={formData[statement.key] === opt.value}
                                    onChange={() => handleInputChange(statement.key, opt.value)}
                                    className="scale-125"
                                />
                            </div>
                        ))}
                    </div>
                ))}
            </div>

            {/* My Time Habits Section */}
            <div className="rounded-lg overflow-hidden border border-blue-200 shadow-sm">
                <div className="bg-[#BFDBFE] p-3 text-center font-bold text-lg text-blue-900 flex items-center justify-center gap-2">
                    <ClockCircleOutlined className="text-xl" />
                    <span>My Time Habits</span>
                </div>

                <div className="grid grid-cols-4 divide-x divide-blue-200 bg-[#EFF6FF]">
                    {/* Column 1 */}
                    <div className="p-4 flex flex-col gap-6">
                        <div className="font-bold text-sm text-center bg-blue-100 p-2 rounded mb-2">What time of the day is best for you...</div>

                        <div>
                            <label className="block text-xs font-semibold mb-1 text-center">...to study?</label>
                            <Input
                                value={formData.best_time_study}
                                onChange={(e) => handleInputChange('best_time_study', e.target.value)}
                                className="text-xs text-center border-blue-200"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold mb-1 text-center">...to play/exercise?</label>
                            <Input
                                value={formData.best_time_play}
                                onChange={(e) => handleInputChange('best_time_play', e.target.value)}
                                className="text-xs text-center border-blue-200"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold mb-1 text-center">...to relax?</label>
                            <Input
                                value={formData.best_time_relax}
                                onChange={(e) => handleInputChange('best_time_relax', e.target.value)}
                                className="text-xs text-center border-blue-200"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold mb-1 text-center">...to spend time with friends and family?</label>
                            <Input
                                value={formData.best_time_friends}
                                onChange={(e) => handleInputChange('best_time_friends', e.target.value)}
                                className="text-xs text-center border-blue-200"
                            />
                        </div>
                    </div>

                    {/* Column 2 */}
                    <div className="p-4 flex flex-col justify-center">
                        <div className="font-bold text-sm text-center bg-blue-100 p-2 rounded mb-4">How much time do you spend on schoolwork after school?</div>
                        <Input.TextArea
                            value={formData.time_schoolwork}
                            onChange={(e) => handleInputChange('time_schoolwork', e.target.value)}
                            className="text-xs text-center border-blue-200 min-h-[100px]"
                            placeholder="e.g., 2 hours"
                        />
                    </div>

                    {/* Column 3 */}
                    <div className="p-4 flex flex-col gap-6">
                        <div>
                            <div className="font-bold text-sm text-center bg-blue-100 p-2 rounded mb-2">How much time do you spend on physical fitness?</div>
                            <Input.TextArea
                                value={formData.time_fitness}
                                onChange={(e) => handleInputChange('time_fitness', e.target.value)}
                                className="text-xs text-center border-blue-200"
                                autoSize={{ minRows: 2 }}
                            />
                        </div>
                        <div>
                            <div className="font-bold text-sm text-center bg-blue-100 p-2 rounded mb-2">How much time do you spend taking care of your emotional wellbeing?</div>
                            <Input.TextArea
                                value={formData.time_emotional_wellbeing}
                                onChange={(e) => handleInputChange('time_emotional_wellbeing', e.target.value)}
                                className="text-xs text-center border-blue-200"
                                autoSize={{ minRows: 2 }}
                            />
                        </div>
                        <div>
                            <div className="font-bold text-sm text-center bg-blue-100 p-2 rounded mb-2">What do you do to improve your physical and mental fitness?</div>
                            <Input.TextArea
                                value={formData.improve_fitness}
                                onChange={(e) => handleInputChange('improve_fitness', e.target.value)}
                                className="text-xs text-center border-blue-200"
                                autoSize={{ minRows: 3 }}
                            />
                        </div>
                    </div>

                    {/* Column 4 */}
                    <div className="p-4 flex flex-col gap-6">
                        <div>
                            <div className="font-bold text-sm text-center bg-blue-100 p-2 rounded mb-2">How much time do you spend on other things? (With friends, on your phone, etc.)</div>
                            <Input.TextArea
                                value={formData.time_other_things}
                                onChange={(e) => handleInputChange('time_other_things', e.target.value)}
                                className="text-xs text-center border-blue-200"
                                autoSize={{ minRows: 2 }}
                            />
                        </div>
                        <div className="mt-auto">
                            <div className="font-bold text-sm text-center bg-blue-100 p-2 rounded mb-2">Do you make to-do lists for your daily tasks? Why / Why not?</div>
                            <Input.TextArea
                                value={formData.make_todo_lists}
                                onChange={(e) => handleInputChange('make_todo_lists', e.target.value)}
                                className="text-xs text-center border-blue-200"
                                autoSize={{ minRows: 4 }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PartA3TimeManagement;